/**
 * 「这个时长档装得下多少东西」—— 创建页时长档下面那句提示的数据源。
 *
 * ═══ 由来 ═══════════════════════════════════════════════════════════════════
 * 2026-08-19 老板走查:「编排的时候是否可以考虑给用户友好提示,比如 15s 的视频推荐
 * 1-2 个卖点或者产品,30s 的又如何推荐」。真机上时长档下面只有一句「时长是目标值,
 * 成片可能有 3 秒左右出入」—— 用户无从判断自己写的那段文案到底装不装得下。
 *
 * 而这些数 **ai 侧早就有**,只是从没告诉过用户:
 *   · 镜数区间 = `app/modules/video/domain/intent_infer.py:shot_budget()`
 *       lo = max(2, ceil(target / _SEC_PER_SHOT_MAX))   # _SEC_PER_SHOT_MAX = 4.0
 *       hi = min(_PANEL_COUNT_CEILING, max(lo, round(target / _SEC_PER_SHOT_MIN)))
 *                                                       # _SEC_PER_SHOT_MIN = 2.0, CEILING = 16
 *     那个函数的 docstring 写着「这是**唯一**产出镜数指引的地方」——所以照它复刻。
 *   · 口播上限 = 写作建议口径 5.85(硬裁上限另见 voiceoverCap 7.28,0913 拆名)
 *     (超了会被**硬裁**,不是软提示)
 *
 * ★为什么复刻公式而不是把 [4,8]/[8,15]/[12,16] 三组结果硬编码:硬编码的话 ai 那边调了
 *   `_SEC_PER_SHOT_MAX` 这边不会有任何动静,而且读代码的人看不出这些数字是哪来的。
 *   本仓的既有口径同此(见 effectiveDuration.ts 对 `_TIER_TARGET_SEC` 的注释)。
 *
 * ★Python 的 round() 是**银行家舍入**,JS 的 Math.round 不是。三个真实档位(15/30/45)
 *   两边结果一致,已在 shotBudget.test.ts 逐档钉死;将来若加了会踩到 .5 的档位,
 *   要回来对一次。
 */

/** 与 ai `intent_infer._SEC_PER_SHOT_MAX` 同源:镜数下限 = target / 这个数。 */
const SEC_PER_SHOT_MAX = 4.0;
/** 与 ai `intent_infer._SEC_PER_SHOT_MIN` 同源:镜数上限 = target / 这个数。 */
const SEC_PER_SHOT_MIN = 2.0;
/**
 * 与 ai `intent_infer._PANEL_COUNT_CEILING` 同源(= system prompt 的 panelCount 天花板)。
 * ★0826 16→24(上架 60s 档):16 是为 ≤45s 定的,60s 在 16 镜下每镜 3.8~4.0s,
 * 被逼成长镜流水账。**这是复刻值,天生会漂** —— `/video/creative-taxonomy` 现在下发
 * `beatPlans[].shotRange`(与 ai shot_budget 同一次计算),消费方应优先用它,
 * 本函数只作为 taxonomy 未就绪时的回退。
 */
const PANEL_COUNT_CEILING = 24;
/**
 * ★「同名两个量」拆名(0913 深夜,二次校准):
 * 这里是**建议写作量**(合身度信号/「装得下多少」建议/容量行)—— 取 5.85
 * ≈ TTS 实测读速 5.6 字/秒 × 小幅上浮,即「舒适念满」的保守口径;
 * **硬裁上限**是另一个量(7.28 = 5.6×语速上限 1.3,连最快语速都念不完才裁),
 * 只归截断预告,见 shared/lib/video/voiceoverCap.VOICEOVER_HARD_CAP_CHARS_PER_SEC。
 * 一度把两者并成 7.28 一把尺 —— 后果:同样的内容量在建议口径下显得「更稀」,
 * 合身度信号变敏感、卖点建议虚涨一档(durationFit/VideoFormatRow 四测实锤)。
 * 建议按舒适口径、裁刀按物理极限,两个量各有各的名字,别再合并。
 */
const VOICEOVER_CHARS_PER_SEC = 5.85;

/**
 * 一个卖点讲清楚大概要多少字口播(含铺垫)。
 *
 * ★这个数是**经验值,不是 ai 侧的常量** —— 与上面四个不同,它没有代码出处。
 *   来源是「一条 15s 的带货短视频讲 1~2 个卖点效果最好」这类行业common sense,
 *   落成 45 字/卖点后,三个档位恰好给出 1-2 / 3-4 / 5-6。
 *   ★上线后应当用真实编排结果校准(拿一批真单看导演实际给每个卖点分了多少字),
 *     校准前它只配当「建议」,不配当硬约束 —— UI 文案也是这么说的。
 */
const CHARS_PER_SELLING_POINT = 45;

/** 目标总秒数 → 建议镜数区间 `[lo, hi]`(与 ai shot_budget 同公式)。 */
/**
 * Python `round()` 的**银行家舍入**(.5 取偶),JS 的 `Math.round` 是 .5 向上。
 *
 * ★本文件头注早就预言了这一天:「三个真实档位(15/30/45)两边结果一致…**将来若加了会踩到 .5
 * 的档位,要回来对一次**」。0826 上架 60s、天花板 16→24 之后,45s 的 `45/2 = 22.5` 不再被
 * 天花板挡住 —— Python 给 22、JS 给 23,**两级指引当场分叉**(ai 按 22 编排、UI 显示 23)。
 * 这就是回来对的那一次。
 */
function roundHalfToEven(x: number): number {
  const f = Math.floor(x);
  const diff = x - f;
  if (diff > 0.5) return f + 1;
  if (diff < 0.5) return f;
  return f % 2 === 0 ? f : f + 1;
}

export function shotBudget(targetSec: number): [number, number] {
  // ★lo 也要过天花板(与 ai 同步):只钳 hi 会让 target 超过 天花板×4 秒后 lo > hi,
  // 区间反转成一句自相矛盾的指引(68s → [17,16])。
  const lo = Math.min(Math.max(2, Math.ceil(targetSec / SEC_PER_SHOT_MAX)), PANEL_COUNT_CEILING);
  const hi = Math.min(
    PANEL_COUNT_CEILING,
    Math.max(lo, roundHalfToEven(targetSec / SEC_PER_SHOT_MIN))
  );
  return [lo, hi];
}

/** 目标总秒数 → 整片口播字数上限(近似:ai 是**逐镜**裁的,建议口径,各镜之和≈总时长×5.85;硬裁在 7.28)。 */
export function voiceoverCharBudget(targetSec: number): number {
  return Math.round(targetSec * VOICEOVER_CHARS_PER_SEC);
}

export interface DurationAdvice {
  /** 建议镜数区间。 */
  shots: [number, number];
  /** 口播字数上限(超了会被硬裁)。 */
  chars: number;
  /** 建议讲几个卖点/产品(经验值,见 CHARS_PER_SELLING_POINT)。 */
  points: [number, number];
}

/**
 * @param contentBeats 引擎节拍表里的**内容位数**(build_tension/turn),来自
 *   `/video/creative-taxonomy` 的 `beatPlans[].contentBeats`。给了就以它为准。
 *
 * ★0826 质感批 ⑤:此前只有下面那条 `CHARS_PER_SELLING_POINT` 经验值一条路 ——
 * 45s 档推出「5~6 个卖点」,而引擎的 45s 硬广节拍表只有 **4 个内容位**。
 * 用户照 UI 写 6 个,引擎按 4 个位子编排,每个都说不透。两个数据源打架,
 * 而那个常量的注释自己写着「校准前它只配当建议」。真值来了就别再用估的。
 */
export function durationAdvice(
  targetSec?: number | null,
  contentBeats?: number | null
): DurationAdvice | null {
  if (!targetSec || targetSec <= 0) return null;
  const chars = voiceoverCharBudget(targetSec);
  if (contentBeats && contentBeats > 0) {
    // 真值:内容位就是「能讲几个点」。不再给区间 —— 区间是估算时代的产物,
    // 节拍表是确定的,给个确定数用户才敢照着写。
    return { shots: shotBudget(targetSec), chars, points: [contentBeats, contentBeats] };
  }
  const n = Math.round(chars / CHARS_PER_SELLING_POINT);
  return {
    shots: shotBudget(targetSec),
    chars,
    points: [Math.max(1, n - 1), Math.max(1, n)],
  };
}
